import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { accountApi } from "./api";

const TOKEN_KEY = "gesung_account_token";
const AccountCtx = createContext(null);

// Map FastAPI error detail (string OR 422 array of {msg}) to a plain string for the UI.
export const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const AccountProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const tok = localStorage.getItem(TOKEN_KEY);
    if (!tok) { setReady(true); return; }
    accountApi.get("/account/me")
      .then(({ data }) => setUser(data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setReady(true));
  }, []);

  const persist = (data) => {
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setUser(data.user);
  };

  const register = useCallback(async (name, email, password) => {
    const { data } = await accountApi.post("/account/register", { name, email, password });
    persist(data);
    return data.user;
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await accountApi.post("/account/login", { email, password });
    persist(data);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  return (
    <AccountCtx.Provider value={{ user, ready, register, login, logout }}>
      {children}
    </AccountCtx.Provider>
  );
};

export const useAccount = () => {
  const ctx = useContext(AccountCtx);
  if (!ctx) throw new Error("useAccount must be used within AccountProvider");
  return ctx;
};
